import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { useLanguage } from '@/i18n/LanguageProvider';

const defaultTestimonials = [
  {
    quote: "The research has evolved on the concept of identity applied to the use of materials and finishes, creating spaces of remarkable sophistication.",
    source: "Architecture Magazine",
    country: "Italy"
  },
  {
    quote: "The materials have been chosen according to a logic that enhances light and matter, emphasizing the details and above all the views that each space offers.",
    source: "Design Review",
    country: "England"
  },
  {
    quote: "Luminous, original and functional. An intervention that transforms vacation homes into experiences overlooking the gulf.",
    source: "Interior Portal",
    country: "Italy"
  }
];

export default function TestimonialSection() {
  const { language } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);

  const { data: testimonials = [] } = useQuery({
    queryKey: ['testimonials'],
    queryFn: () => base44.entities.Testimonial.list(),
    initialData: []
  });

  useEffect(() => {
    if (testimonials.length > 0) {
      const timer = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % testimonials.length);
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [testimonials.length]);

  if (testimonials.length === 0) {
    return null;
  }

  return (
    <section className="py-20 md:py-32 bg-white">
      <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
        <h2 className="text-xs tracking-widest-custom uppercase text-[#74675a] mb-12">
          Press
        </h2>

        <div className="relative min-h-[200px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.6 }}
              className="absolute inset-0"
            >
              <blockquote className="mb-8">
                <p className="font-serif text-xl md:text-2xl lg:text-3xl text-[#2d2d2d] leading-relaxed italic">
                  "{testimonials[currentIndex]?.quote}"
                </p>
              </blockquote>
              <div>
                <p className="text-sm font-medium text-[#2d2d2d]">
                  {testimonials[currentIndex]?.source}
                </p>
                <p className="text-xs text-[#5f5f5f] mt-1">
                  {language === 'en' && testimonials[currentIndex]?.country === 'Italia' ? 'Italy' : testimonials[currentIndex]?.country}
                </p>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Indicators */}
        <div className="flex justify-center gap-2 mt-12">
          {testimonials.map((_, index) => (
            <button
              type="button"
              key={index}
              onClick={() => setCurrentIndex(index)}
              aria-label={`Mostra testimonianza ${index + 1}`}
              aria-current={index === currentIndex ? 'true' : undefined}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentIndex ? 'bg-[#74675a] w-6' : 'bg-[#d4d4d4]'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}